import { useState, type FormEvent } from "react";

const FORM_ACTION = "/api/consult.php";

const inputClass =
  "w-full rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get("phone_alt") as string) ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key === "phone_alt") return;
      payload[key] = value as string;
    });

    setStatus("submitting");
    setFormError("");

    try {
      const res = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const parsed: { ok?: boolean; error?: string } | null = await res.json().catch(() => null);

      if (res.ok && parsed?.ok) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(parsed?.error || "전송에 문제가 발생했습니다. 다시 시도해 주세요.");
      }
    } catch {
      setStatus("error");
      setFormError("네트워크 오류가 발생했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      className="rounded-2xl border border-background-200 bg-background-100/60 p-6 md:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label
            htmlFor="name"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            이름 <span className="text-primary-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="학생 이름 또는 학부모님 성함"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="phone"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            연락처 <span className="text-primary-600">*</span>
          </label>
          <input
            id="phone"
            name="phone"
            type="tel"
            required
            placeholder="010-0000-0000"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="email"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            이메일
          </label>
          <input
            id="email"
            name="email"
            type="email"
            placeholder="example@email.com"
            className={inputClass}
          />
        </div>

        <div>
          <label
            htmlFor="grade"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            학년 <span className="text-primary-600">*</span>
          </label>
          <select id="grade" name="grade" required defaultValue="" className={inputClass}>
            <option value="" disabled>
              학년을 선택해 주세요
            </option>
            <option value="초등부">초등부</option>
            <option value="중등부">중등부</option>
            <option value="고등부">고등부</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label
            htmlFor="prefer"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            상담 희망 방식
          </label>
          <select id="prefer" name="prefer" defaultValue="전화 상담" className={inputClass}>
            <option value="전화 상담">전화 상담</option>
            <option value="방문 상담">방문 상담</option>
            <option value="온라인 상담">온라인 상담</option>
          </select>
        </div>

        <div className="sm:col-span-2">
          <label
            htmlFor="message"
            className="mb-1.5 block text-sm font-bold text-foreground-950"
          >
            문의 내용
          </label>
          <textarea
            id="message"
            name="message"
            rows={5}
            maxLength={500}
            placeholder="현재 성적, 고민하고 계신 점, 희망하는 학습 목표 등을 알려주세요. (최대 500자)"
            className={`${inputClass} resize-none`}
          />
        </div>
      </div>

      <input
        type="text"
        name="phone_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="hp-field"
      />

      <div className="mt-7">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-full bg-primary-500 px-7 py-4 text-sm font-bold text-foreground-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto cursor-pointer"
        >
          {status === "submitting" && (
            <i className="ri-loader-4-line animate-spin" />
          )}
          <span className="relative z-10">
            {status === "submitting" ? "전송 중..." : "상담 신청하기"}
          </span>
          {status !== "submitting" && <i className="ri-arrow-right-line relative z-10" />}
        </button>

        {status === "success" && (
          <p className="mt-4 flex items-center gap-2 text-sm font-bold text-primary-700">
            <i className="ri-checkbox-circle-line text-lg" />
            상담 신청이 접수되었습니다. 담당 선생님이 곧 연락드리겠습니다.
          </p>
        )}
        {status === "error" && (
          <p className="mt-4 flex items-center gap-2 text-sm font-bold text-red-600">
            <i className="ri-error-warning-line text-lg" />
            {formError}
          </p>
        )}
      </div>
    </form>
  );
}